import { Component, LLMProvider, Message, T_RESPONSE, CollectionRegistry, Tool, DashCompModelConfig } from "../types";
import { logger } from "../utils/logger";
import { llmUsageLogger } from "../utils/llm-usage-logger";

import { ReportCompResponse } from "./types";
import { sendReportCompResponse } from "./utils";
import { generateReportComponents } from "./generate-report";
import { dashboardConversationHistory } from "../dashComp/dashboard-conversation-history";

// Re-export types for external use
export * from "./types";

/**
 * Process REPORT_COMP_REQ request
 */
export const processReportCompRequest = async (
	data: any,
	components: Component[],
	_sendMessage: (message: Message) => void,
	anthropicApiKey?: string,
	groqApiKey?: string,
	geminiApiKey?: string,
	openaiApiKey?: string,
	llmProviders?: LLMProvider[],
	collections?: CollectionRegistry,
	tools?: Tool[],
	modelConfig?: DashCompModelConfig
): Promise<ReportCompResponse> => {
	const errors: string[] = [];

	// Parse incoming message data — reuse the same payload structure
	const id = data?.id;
	const payload = data?.payload;
	const prompt = payload?.prompt;
	const wsId = data?.from?.id || 'unknown';
	const reportId = payload?.reportId;
	const userId = payload?.userId;

	// Get conversation history scoped to this user + report
	const conversationHistory = reportId
		? dashboardConversationHistory.getHistory(reportId, userId)
		: undefined;

	// Reset log files for this request
	const promptContext = `REPORT_COMP: ${prompt?.substring(0, 50)}${(prompt?.length || 0) > 50 ? '...' : ''}`;
	llmUsageLogger.resetLogFile(promptContext);

	if (!prompt) {
		errors.push('Prompt is required');
		return { success: false, errors, id, wsId };
	}

	logger.info(`[REPORT_COMP_REQ] Processing report request: "${prompt.substring(0, 80)}..."`);
	logger.info(`[REPORT_COMP_REQ] Available: ${components?.length || 0} components, ${tools?.length || 0} tools`);

	if (!components || components.length === 0) {
		logger.warn('[REPORT_COMP_REQ] No components available');
		return {
			success: false,
			errors: ['No components available. Please ensure components are loaded.'],
			id,
			wsId
		};
	}

	const llmResponse = await generateReportComponents(
		prompt,
		components,
		anthropicApiKey,
		groqApiKey,
		geminiApiKey,
		openaiApiKey,
		llmProviders,
		collections,
		tools,
		modelConfig,
		conversationHistory,
		userId
	);

	// Store conversation entry for this user's report on success
	if (llmResponse.success && reportId && prompt) {
		const comps = llmResponse.data?.components;
		let summary = 'No components generated';
		if (Array.isArray(comps) && comps.length > 0) {
			summary = comps.map((c: any) => `${c.type || 'Component'}: ${c.name || 'unnamed'}`).join(', ');
			summary = `Generated ${comps.length} components: ${summary}`;
		}
		dashboardConversationHistory.addEntry(reportId, prompt, summary, userId);
	}

	llmUsageLogger.logSessionSummary(`REPORT_COMP: ${prompt?.substring(0, 30)}`);

	return {
		success: llmResponse.success,
		data: llmResponse.data,
		errors: llmResponse.errors,
		id,
		wsId
	};
};

/**
 * Handle REPORT_COMP_REQ WebSocket message
 */
export async function handleReportCompRequest(
	data: any,
	components: Component[],
	sendMessage: (message: Message) => void,
	anthropicApiKey?: string,
	groqApiKey?: string,
	geminiApiKey?: string,
	openaiApiKey?: string,
	llmProviders?: LLMProvider[],
	collections?: CollectionRegistry,
	tools?: Tool[],
	modelConfig?: DashCompModelConfig
): Promise<void> {
	const response = await processReportCompRequest(
		data,
		components,
		sendMessage,
		anthropicApiKey,
		groqApiKey,
		geminiApiKey,
		openaiApiKey,
		llmProviders,
		collections,
		tools,
		modelConfig
	);

	sendReportCompResponse(
		response.id || data.id,
		{
			success: response.success,
			errors: response.errors,
			data: response.data
		},
		sendMessage,
		response.wsId || data.from?.id
	);

	logger.info(`[REPORT_COMP_REQ] Response sent to client ${response.wsId || data.from?.id}`);
}
